import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBenefitProgramsV1PoolDefinition from './benefit-programs-v1-pool-definition-pool-definitions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface PoolDefinition {
  /**
   * Pool definition ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the pool definition is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the pool definition.
   *
   * Ignored when creating a pool definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the pool definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the pool definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Pool definition name.
   * @maxLength 128
   */
  displayName?: string;
  /**
   * List of program definition IDs that are associated with this pool definition.
   * @format GUID
   * @maxSize 20
   */
  programDefinitionIds?: string[];
  /** Pool definition benefits and settings. */
  details?: Details;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @immutable
   * @readonly
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string | null;
  /**
   * Custom field data for the pool definition object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Program definitions that this pool definition is associated with.
   * @readonly
   * @maxSize 20
   */
  programDefinitions?: ProgramDefinitionInfo[];
  /**
   * Pool description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
}

export interface Details {
  /**
   * List of benefits.
   * @maxSize 10
   */
  benefits?: Benefit[];
  /**
   * Credit settings.
   *
   * If this object is empty, you can't set a price for the benefit.
   */
  creditConfiguration?: CreditConfiguration;
  /** Additional information relating to this object. */
  additionalData?: Record<string, any> | null;
}

export interface Benefit {
  /**
   * Unique identifier for this benefit.
   *
   * This key is consistent across the pool definition and all associated pools that contain this benefit.
   * @maxLength 64
   */
  benefitKey?: string;
  /**
   * ID that is used to associated items with this benefit.
   * @format GUID
   * @immutable
   * @readonly
   */
  itemSetId?: string | null;
  /**
   * Price of the benefit in credits. The price is the same for all of this benefit's items.
   * @decimalValue options { gte:0, maxScale:4 }
   */
  price?: string | null;
  /** Additional information for this benefit. */
  additionalData?: Record<string, any> | null;
  /**
   * ID of the app providing the benefit items.
   * @format GUID
   * @immutable
   */
  providerAppId?: string | null;
  /**
   * Benefit name.
   * @maxLength 40
   */
  displayName?: string | null;
  /**
   * Benefit description.
   * @maxLength 255
   */
  description?: string | null;
}

export interface PolicyExpression extends PolicyExpressionExpressionOneOf {
  /** Benefits can be redeemed if the expression in this object is not fulfilled. */
  operatorNotOptions?: PolicyExpressionNot;
  /** Benefits can be redeemed if all the expression in this object's array are fulfilled. */
  operatorAndOptions?: PolicyExpressionAnd;
  /** Benefits can be redeemed if at least one of the expressions in this object's array is fulfilled. */
  operatorOrOptions?: PolicyExpressionOr;
  /** Defines when benefits can be redeemed or how many benefits can be redeemed in a specific time period. */
  policyOptions?: Policy;
  /** Policy expression type. */
  type?: PolicyExpressionTypeWithLiterals;
}

/** @oneof */
export interface PolicyExpressionExpressionOneOf {
  /** Benefits can be redeemed if the expression in this object is not fulfilled. */
  operatorNotOptions?: PolicyExpressionNot;
  /** Benefits can be redeemed if all the expression in this object's array are fulfilled. */
  operatorAndOptions?: PolicyExpressionAnd;
  /** Benefits can be redeemed if at least one of the expressions in this object's array is fulfilled. */
  operatorOrOptions?: PolicyExpressionOr;
  /** Defines when benefits can be redeemed or how many benefits can be redeemed in a specific time period. */
  policyOptions?: Policy;
}

export enum PolicyExpressionType {
  /** Use with `operatorNotOptions`. */
  OPERATOR_NOT = 'OPERATOR_NOT',
  /** Use with `operatorAndOptions`. */
  OPERATOR_AND = 'OPERATOR_AND',
  /** Use with `operatorOrOptions`. */
  OPERATOR_OR = 'OPERATOR_OR',
  /** Use with `policyOptions`. */
  POLICY = 'POLICY',
}

/** @enumType */
export type PolicyExpressionTypeWithLiterals =
  | PolicyExpressionType
  | 'OPERATOR_NOT'
  | 'OPERATOR_AND'
  | 'OPERATOR_OR'
  | 'POLICY';

export interface PolicyExpressionNot {
  /** Policy expression. If this expression is not fulfilled, benefits can be redeemed. */
  expression?: PolicyExpression;
}

export interface PolicyExpressionAnd {
  /**
   * Array of policy expressions. If all expressions are fulfilled, benefits can be redeemed.
   * @minSize 2
   * @maxSize 10
   */
  expressions?: PolicyExpression[];
}

export interface PolicyExpressionOr {
  /**
   * Array of policy expressions. If at least one expression is fulfilled, benefits can be redeemed.
   * @minSize 2
   * @maxSize 10
   */
  expressions?: PolicyExpression[];
}

export interface Policy extends PolicyPolicyOneOf {
  /**
   * Defines an interval during which the policy expression is fulfilled.
   *
   * If `fromWeekDay` and `toWeekDay` are defined, this interval applies weekly. Otherwise, it applies daily.
   */
  fixedIntervalOptions?: FixedIntervalPolicy;
  /** Maximum amount of times a benefit can be redeemed during a specified time period. */
  rateLimitedOptions?: RateLimitedPolicy;
  /** Custom policy defined by a different app. */
  customOptions?: CustomPolicy;
  /** Policy type. */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface PolicyPolicyOneOf {
  /**
   * Defines an interval during which the policy expression is fulfilled.
   *
   * If `fromWeekDay` and `toWeekDay` are defined, this interval applies weekly. Otherwise, it applies daily.
   */
  fixedIntervalOptions?: FixedIntervalPolicy;
  /** Maximum amount of times a benefit can be redeemed during a specified time period. */
  rateLimitedOptions?: RateLimitedPolicy;
  /** Custom policy defined by a different app. */
  customOptions?: CustomPolicy;
}

export enum Type {
  /** Use with `fixedIntervalOptions`. */
  FIXED_INTERVAL = 'FIXED_INTERVAL',
  /** Use with `rateLimitedOptions`. */
  RATE_LIMITED = 'RATE_LIMITED',
  /** Use with `customOptions`. */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'FIXED_INTERVAL'
  | 'RATE_LIMITED'
  | 'CUSTOM';

export interface FixedIntervalPolicy {
  /** Weekday that this interval starts from. If this field is defined, then `toWeekDay` is required. */
  fromWeekDay?: WeekDayWithLiterals;
  /** Weekday that this interval ends at. If this field is defined, then `fromWeekDay` is required. */
  toWeekDay?: WeekDayWithLiterals;
  /**
   * Hour that this interval starts from. If this field is defined, then `toHour` is required.
   * @max 23
   */
  fromHour?: number | null;
  /**
   * Hour that this interval ends at. If this field is defined, then `fromHour` is required.
   * @max 23
   */
  toHour?: number | null;
  /**
   * Minute that this interval starts from. If this field is defined, then `toMinute` is required.
   * @max 59
   */
  fromMinute?: number | null;
  /**
   * Minute that this interval ends at. If this field is defined, then `fromMinute` is required.
   * @max 59
   */
  toMinute?: number | null;
}

export enum WeekDay {
  /** Unknown weekday. */
  UNKNOWN = 'UNKNOWN',
  /** Monday. */
  MONDAY = 'MONDAY',
  /** Tuesday. */
  TUESDAY = 'TUESDAY',
  /** Wednesday. */
  WEDNESDAY = 'WEDNESDAY',
  /** Thursday. */
  THURSDAY = 'THURSDAY',
  /** Friday. */
  FRIDAY = 'FRIDAY',
  /** Saturday. */
  SATURDAY = 'SATURDAY',
  /** Sunday. */
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type WeekDayWithLiterals =
  | WeekDay
  | 'UNKNOWN'
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface RateLimitedPolicy {
  /** Maximum number of times a benefit can be redeemed per specified time period. */
  times?: number;
  /** Time period for the rate limit, resets at the start of each period. */
  timePeriod?: TimePeriodWithLiterals;
  /**
   * Unique identifier for the quota resource. Set automatically when creating a policy.
   * Updating RateLimitedPolicy `time period` or `times` will result in a quota reset.
   * @immutable
   * @readonly
   * @format GUID
   */
  quotaId?: string | null;
}

export enum TimePeriod {
  /** Day, starting at midnight. */
  DAY = 'DAY',
  /** Calendar month, starting on the first day of the month. */
  MONTH = 'MONTH',
}

/** @enumType */
export type TimePeriodWithLiterals = TimePeriod | 'DAY' | 'MONTH';

/** Custom policy as implemented by the Entitlement Policy Provider */
export interface CustomPolicy {
  /**
   * Policy ID.
   * @format GUID
   */
  _id?: string;
  /**
   * ID of the app providing the policy.
   * @format GUID
   */
  appId?: string | null;
  /** Additional information for this custom policy. */
  additionalData?: Record<string, any> | null;
}

export interface CreditConfiguration {
  /**
   * Initial available amount for associated balances.
   * @decimalValue options { gte:0, maxScale:4 }
   */
  amount?: string;
  /** Rollover settings. */
  rolloverConfiguration?: RolloverConfiguration;
  /**
   * Credit unit display name.
   * @maxLength 32
   */
  unitDisplayName?: string | null;
  /**
   * Credit unit type.
   * @internal
   * @maxLength 16
   */
  unitType?: string | null;
}

export interface RolloverConfiguration {
  /** Whether unused credits roll over to a new cycle when a program renews. */
  enabled?: boolean | null;
  /**
   * Maximum number of credits that can roll over to the next cycle when a program renews.
   * @decimalValue options { gte:0, maxScale:4 }
   */
  balanceCap?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ProgramDefinitionInfo {
  /**
   * Program definition ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Program definition external ID.
   * @format GUID
   * @readonly
   */
  externalId?: string | null;
  /**
   * Program definition name.
   * @internal
   * @maxLength 64
   */
  displayName?: string | null;
  /**
   * Program definition description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
}

export interface PoolDefinitionAddedToProgramDefinition {
  /** PoolDefinition that was added to the program definition */
  poolDefinition?: PoolDefinition;
  /**
   * Program Definition that the pool definition was added to
   * @format GUID
   */
  programDefinitionId?: string;
  /** Type of cascade */
  cascadeType?: CascadeWithLiterals;
}

export enum Cascade {
  /** Unknown cascade. */
  UNKNOWN_CASCADE = 'UNKNOWN_CASCADE',
  /** Changes are applied to existing programs and pools when they are next renewed. */
  NEXT_RENEWAL = 'NEXT_RENEWAL',
  /** Changes are applied to associated programs and pools immediately. */
  IMMEDIATELY = 'IMMEDIATELY',
  /** Changes are not applied to existing associated programs and pools. They are only applied to future programs and pools. */
  FUTURE_PROVISIONS = 'FUTURE_PROVISIONS',
}

/** @enumType */
export type CascadeWithLiterals =
  | Cascade
  | 'UNKNOWN_CASCADE'
  | 'NEXT_RENEWAL'
  | 'IMMEDIATELY'
  | 'FUTURE_PROVISIONS';

export interface PoolDefinitionRemovedFromProgramDefinition {
  /** PoolDefinition that was removed from the program definition */
  poolDefinition?: PoolDefinition;
  /**
   * Program Definition that the pool definition was removed from
   * @format GUID
   */
  programDefinitionId?: string;
  /** Type of cascade */
  cascadeType?: CascadeWithLiterals;
}

/** This event is needed to support legacy benefit notification event. Should be only consumed in the proxy. */
export interface PoolDefinitionUpdatedProxySupport {
  /** PoolDefinition after the update */
  currentPoolDefinition?: PoolDefinition;
  /** PoolDefinition before the update */
  previousPoolDefinition?: PoolDefinition;
}

/** This event is needed to support legacy benefit notification event. Should be only consumed in the proxy. */
export interface PoolDefinitionDeletedProxySupport {
  /** PoolDefinition which was deleted */
  deletedPoolDefinition?: PoolDefinition;
}

export interface PoolDefinitionTranslated {
  /** The translated pool definition. */
  poolDefinition?: PoolDefinition;
  /**
   * The language code of the translation.
   * @maxLength 16
   */
  language?: string;
}

export interface PoolDefinitionTranslationDeleted {
  /**
   * The ID of the pool definition.
   * @format GUID
   */
  poolDefinitionId?: string;
  /**
   * The language code of the translation.
   * @maxLength 16
   */
  language?: string;
  /** The current revision of the pool definition. */
  poolDefinitionRevision?: string;
}

export interface CreatePoolDefinitionRequest {
  /** Pool definition to create. */
  poolDefinition: PoolDefinition;
  /**
   * Determines when the changes to the program definitions containing this pool definition will be applied to associated programs.
   *
   * Required if the `poolDefinition.programDefinitionIds` parameter is defined.
   */
  cascade?: CascadeWithLiterals;
}

export interface CreatePoolDefinitionResponse {
  /** Created pool definition. */
  poolDefinition?: PoolDefinition;
}

export interface BulkCreatePoolDefinitionsRequest {
  /**
   * Pool definitions to create.
   * @minSize 1
   * @maxSize 100
   */
  poolDefinitions: PoolDefinition[];
  /**
   * Determines when the changes to the program definitions containing these pool definitions will be applied to associated programs.
   *
   * Required if the `poolDefinition.programDefinitionIds` parameter is defined.
   */
  cascade?: CascadeWithLiterals;
  /**
   * Whether to return the created pool definitions.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreatePoolDefinitionsResponse {
  /**
   * List of created pool definitions and associated metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkPoolDefinitionResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkPoolDefinitionResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Pool definition. */
  item?: PoolDefinition;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface UpdatePoolDefinitionRequest {
  /** Pool definition to update. */
  poolDefinition: PoolDefinition;
  /** *Required.** Determines when the changes to this pool definition and program definitions containing it will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
}

export interface UpdatePoolDefinitionResponse {
  /** Updated pool definition. */
  poolDefinition?: PoolDefinition;
}

export interface BulkUpdatePoolDefinitionsRequest {
  /**
   * Pool definitions to update.
   * @minSize 1
   * @maxSize 100
   */
  poolDefinitions?: MaskedPoolDefinition[];
  /** *Required.** Determines when the changes to these pool definitions program definitions containing them will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
  /**
   * Whether to return the updated pool definitions.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface MaskedPoolDefinition {
  /** Pool definition to update. */
  poolDefinition?: PoolDefinition;
  /** Explicit list of fields to update. */
  fieldMask?: string[];
}

export interface BulkUpdatePoolDefinitionsResponse {
  /**
   * List of updated pool definitions and associated metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkPoolDefinitionResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DeletePoolDefinitionRequest {
  /**
   * ID of the pool definition to delete.
   * @format GUID
   */
  poolDefinitionId: string;
  /** *Required**. Determines when the changes to this pool definition the program definitions containing it will be applied to associated programs and pools. */
  cascade: CascadeWithLiterals;
}

export interface DeletePoolDefinitionResponse {}

export interface BulkDeletePoolDefinitionsRequest {
  /**
   * List of IDs of the pool definitions to delete.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  poolDefinitionIds: string[];
  /** *Required**. Determines when the changes to these pool definitions the program definitions containing it will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
}

export interface BulkDeletePoolDefinitionsResponse {
  /**
   * List of deleted pool definitions and associated metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkPoolDefinitionResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface GetPoolDefinitionRequest {
  /**
   * ID of the pool definition to retrieve.
   * @format GUID
   */
  poolDefinitionId: string;
}

export interface GetPoolDefinitionResponse {
  /** Retrieved pool definition. */
  poolDefinition?: PoolDefinition;
}

export interface QueryPoolDefinitionsRequest {
  /** Filter, sort, and paging to apply to the query. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending sort order. */
  ASC = 'ASC',
  /** Descending sort order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPoolDefinitionsResponse {
  /** List of retrieved pool definitions. */
  poolDefinitions?: PoolDefinition[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AddPoolDefinitionToProgramDefinitionRequest {
  /**
   * ID of the pool definition to add to the program definition.
   * @format GUID
   */
  poolDefinitionId: string;
  /**
   * ID of the program definition to add the pool definition to.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition and pool definition will be applied to associated programs and pools. */
  cascade: CascadeWithLiterals;
}

export interface AddPoolDefinitionToProgramDefinitionResponse {
  /** Updated pool definition. */
  poolDefinition?: PoolDefinition;
}

export interface RemovePoolDefinitionFromProgramDefinitionRequest {
  /**
   * ID of the pool definition to remove from the program definition.
   * @format GUID
   */
  poolDefinitionId: string;
  /**
   * ID of the program definition to remove the pool definition from.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition and pool definition will be applied to associated programs and pools. */
  cascade: CascadeWithLiterals;
}

export interface RemovePoolDefinitionFromProgramDefinitionResponse {
  /** Updated pool definition. */
  poolDefinition?: PoolDefinition;
}

export interface FindPoolDefinitionsByProgramDefinitionRequest {
  /**
   * ID of the program definition to retrieve its pool definitions.
   * @format GUID
   */
  programDefinitionId: string;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   */
  namespace: string;
}

export interface FindPoolDefinitionsByProgramDefinitionResponse {
  /** Retrieved pool definitions. */
  poolDefinitions?: PoolDefinition[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type UpdatePoolDefinitionValidationErrors =
  | {
      ruleName?: 'cascade_is_required';
    }
  | {
      ruleName?: 'cascade_value_not_supported';
    };
/** @docsIgnore */
export type BulkUpdatePoolDefinitionsValidationErrors =
  | {
      ruleName?: 'cascade_is_required';
    }
  | {
      ruleName?: 'cascade_value_not_supported';
    };
/** @docsIgnore */
export type AddPoolDefinitionToProgramDefinitionValidationErrors =
  | {
      ruleName?: 'cascade_is_required';
    }
  | {
      ruleName?: 'cascade_value_not_supported';
    };
/** @docsIgnore */
export type RemovePoolDefinitionFromProgramDefinitionValidationErrors =
  | {
      ruleName?: 'cascade_is_required';
    }
  | {
      ruleName?: 'cascade_value_not_supported';
    };

/**
 * Creates a pool definition.
 * @param poolDefinition - Pool definition to create.
 * @public
 * @requiredField poolDefinition
 * @requiredField poolDefinition.details
 * @requiredField poolDefinition.details.benefits
 * @requiredField poolDefinition.details.benefits.benefitKey
 * @requiredField poolDefinition.details.benefits.providerAppId
 * @requiredField poolDefinition.namespace
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_CREATE
 * @applicableIdentity APP
 * @returns Created pool definition.
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.CreatePoolDefinition
 */
export async function createPoolDefinition(
  poolDefinition: NonNullablePaths<
    PoolDefinition,
    | `details`
    | `details.benefits`
    | `details.benefits.${number}.benefitKey`
    | `details.benefits.${number}.providerAppId`
    | `namespace`,
    5
  >,
  options?: CreatePoolDefinitionOptions
): Promise<
  NonNullablePaths<
    PoolDefinition,
    | `displayName`
    | `programDefinitionIds`
    | `details.benefits`
    | `details.benefits.${number}.benefitKey`
    | `details.creditConfiguration.amount`
    | `programDefinitions`
    | `programDefinitions.${number}._id`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinition: poolDefinition,
    cascade: options?.cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.createPoolDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.poolDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinition: '$[0]',
          cascade: '$[1].cascade',
        },
        singleArgumentUnchanged: false,
      },
      ['poolDefinition', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreatePoolDefinitionOptions {
  /**
   * Determines when the changes to the program definitions containing this pool definition will be applied to associated programs.
   *
   * Required if the `poolDefinition.programDefinitionIds` parameter is defined.
   */
  cascade?: CascadeWithLiterals;
}

/**
 * Creates pool definitions.
 * @param poolDefinitions - Pool definitions to create.
 * @public
 * @requiredField poolDefinitions
 * @requiredField poolDefinitions.details
 * @requiredField poolDefinitions.details.benefits
 * @requiredField poolDefinitions.details.benefits.benefitKey
 * @requiredField poolDefinitions.details.benefits.providerAppId
 * @requiredField poolDefinitions.namespace
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_CREATE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkCreatePoolDefinitions
 */
export async function bulkCreatePoolDefinitions(
  poolDefinitions: NonNullablePaths<
    PoolDefinition,
    | `details`
    | `details.benefits`
    | `details.benefits.${number}.benefitKey`
    | `details.benefits.${number}.providerAppId`
    | `namespace`,
    5
  >[],
  options?: BulkCreatePoolDefinitionsOptions
): Promise<
  NonNullablePaths<
    BulkCreatePoolDefinitionsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.displayName`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitions: poolDefinitions,
    cascade: options?.cascade,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.bulkCreatePoolDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinitions: '$[0]',
          cascade: '$[1].cascade',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitions', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreatePoolDefinitionsOptions {
  /**
   * Determines when the changes to the program definitions containing these pool definitions will be applied to associated programs.
   *
   * Required if the `poolDefinition.programDefinitionIds` parameter is defined.
   */
  cascade?: CascadeWithLiterals;
  /**
   * Whether to return the created pool definitions.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Updates a pool definition.
 *
 * Each time the pool definition is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the pool definition.
 * This ensures you're working with the latest pool definition
 * and prevents unintended overwrites.
 * @param _id - Pool definition ID.
 * @public
 * @requiredField _id
 * @requiredField poolDefinition
 * @requiredField poolDefinition.revision
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_UPDATE
 * @applicableIdentity APP
 * @returns Updated pool definition.
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.UpdatePoolDefinition
 */
export async function updatePoolDefinition(
  _id: string,
  poolDefinition: NonNullablePaths<UpdatePoolDefinition, `revision`, 2>,
  options?: UpdatePoolDefinitionOptions
): Promise<
  NonNullablePaths<
    PoolDefinition,
    | `displayName`
    | `programDefinitionIds`
    | `details.benefits`
    | `details.benefits.${number}.benefitKey`
    | `details.creditConfiguration.amount`
    | `programDefinitions`
    | `programDefinitions.${number}._id`,
    5
  > & {
    __validationErrorsType?: UpdatePoolDefinitionValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinition: { ...poolDefinition, id: _id },
    cascade: options?.cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.updatePoolDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.poolDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { poolDefinition: '$[1]' },
        explicitPathsToArguments: {
          'poolDefinition.id': '$[0]',
          cascade: '$[2].cascade',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'poolDefinition', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePoolDefinition {
  /**
   * Pool definition ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the pool definition is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the pool definition.
   *
   * Ignored when creating a pool definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the pool definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the pool definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Pool definition name.
   * @maxLength 128
   */
  displayName?: string;
  /**
   * List of program definition IDs that are associated with this pool definition.
   * @format GUID
   * @maxSize 20
   */
  programDefinitionIds?: string[];
  /** Pool definition benefits and settings. */
  details?: Details;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @immutable
   * @readonly
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string | null;
  /**
   * Custom field data for the pool definition object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Program definitions that this pool definition is associated with.
   * @readonly
   * @maxSize 20
   */
  programDefinitions?: ProgramDefinitionInfo[];
  /**
   * Pool description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
}

export interface UpdatePoolDefinitionOptions {
  /** Required.** Determines when the changes to this pool definition and program definitions containing it will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
}

/**
 * Updates pool definitions.
 * @public
 * @requiredField options.poolDefinitions.poolDefinition
 * @requiredField options.poolDefinitions.poolDefinition._id
 * @requiredField options.poolDefinitions.poolDefinition.revision
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkUpdatePoolDefinitions
 */
export async function bulkUpdatePoolDefinitions(
  options?: NonNullablePaths<
    BulkUpdatePoolDefinitionsOptions,
    | `poolDefinitions.${number}.poolDefinition`
    | `poolDefinitions.${number}.poolDefinition._id`
    | `poolDefinitions.${number}.poolDefinition.revision`,
    5
  >
): Promise<
  NonNullablePaths<
    BulkUpdatePoolDefinitionsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.displayName`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __validationErrorsType?: BulkUpdatePoolDefinitionsValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitions: options?.poolDefinitions,
    cascade: options?.cascade,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.bulkUpdatePoolDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinitions: '$[0].poolDefinitions',
          cascade: '$[0].cascade',
          returnEntity: '$[0].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePoolDefinitionsOptions {
  /**
   * Pool definitions to update.
   * @minSize 1
   * @maxSize 100
   */
  poolDefinitions?: MaskedPoolDefinition[];
  /** Required.** Determines when the changes to these pool definitions program definitions containing them will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
  /**
   * Whether to return the updated pool definitions.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Deletes a pool definition.
 * @param poolDefinitionId - ID of the pool definition to delete.
 * @param cascade - *Required**. Determines when the changes to this pool definition the program definitions containing it will be applied to associated programs and pools.
 * @public
 * @requiredField cascade
 * @requiredField poolDefinitionId
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_DELETE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.DeletePoolDefinition
 */
export async function deletePoolDefinition(
  poolDefinitionId: string,
  cascade: CascadeWithLiterals
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitionId: poolDefinitionId,
    cascade: cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.deletePoolDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { poolDefinitionId: '$[0]', cascade: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitionId', 'cascade']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes pool definitions.
 * @param poolDefinitionIds - List of IDs of the pool definitions to delete.
 * @public
 * @requiredField poolDefinitionIds
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_DELETE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkDeletePoolDefinitions
 */
export async function bulkDeletePoolDefinitions(
  poolDefinitionIds: string[],
  options?: BulkDeletePoolDefinitionsOptions
): Promise<
  NonNullablePaths<
    BulkDeletePoolDefinitionsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.displayName`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitionIds: poolDefinitionIds,
    cascade: options?.cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.bulkDeletePoolDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinitionIds: '$[0]',
          cascade: '$[1].cascade',
        },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitionIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeletePoolDefinitionsOptions {
  /** Required**. Determines when the changes to these pool definitions the program definitions containing it will be applied to associated programs and pools. */
  cascade?: CascadeWithLiterals;
}

/**
 * Retrieves a pool definition.
 * @param poolDefinitionId - ID of the pool definition to retrieve.
 * @public
 * @requiredField poolDefinitionId
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_READ
 * @applicableIdentity APP
 * @returns Retrieved pool definition.
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.GetPoolDefinition
 */
export async function getPoolDefinition(
  poolDefinitionId: string
): Promise<
  NonNullablePaths<
    PoolDefinition,
    | `displayName`
    | `programDefinitionIds`
    | `details.benefits`
    | `details.benefits.${number}.benefitKey`
    | `details.creditConfiguration.amount`
    | `programDefinitions`
    | `programDefinitions.${number}._id`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitionId: poolDefinitionId,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.getPoolDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.poolDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { poolDefinitionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of pool definitions.
 *
 * The Query Pool Definitions method builds a query to retrieve a list of pool definitions and returns a `PoolDefinitionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `PoolDefinitionsQueryBuilder` methods onto the query. `PoolDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Pool Definitions returns.
 *
 * Query Pool Definitions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `PoolDefinitionsQueryResult`.
 * @public
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.QueryPoolDefinitions
 */
export function queryPoolDefinitions(
  options?: QueryPoolDefinitionsOptions
): PoolDefinitionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PoolDefinition,
    'CURSOR',
    QueryPoolDefinitionsRequest,
    QueryPoolDefinitionsResponse
  >({
    func: async (payload: QueryPoolDefinitionsRequest) => {
      const reqOpts =
        ambassadorWixBenefitProgramsV1PoolDefinition.queryPoolDefinitions({
          ...payload,
          ...(options ?? {}),
        });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPoolDefinitionsRequest['query']) => {
      const args = [query, options] as [
        QueryPoolDefinitionsRequest['query'],
        QueryPoolDefinitionsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPoolDefinitionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.poolDefinitions,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryPoolDefinitionsOptions {}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PoolDefinitionsQueryResult extends QueryCursorResult {
  items: PoolDefinition[];
  query: PoolDefinitionsQueryBuilder;
  next: () => Promise<PoolDefinitionsQueryResult>;
  prev: () => Promise<PoolDefinitionsQueryResult>;
}

export interface PoolDefinitionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: string
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any[]
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'programDefinitionIds',
    value: any[]
  ) => PoolDefinitionsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: any
  ) => PoolDefinitionsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace',
    value: boolean
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace'
    >
  ) => PoolDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | 'programDefinitionIds'
      | 'details.benefits.itemSetId'
      | 'details.benefits.providerAppId'
      | 'namespace'
    >
  ) => PoolDefinitionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => PoolDefinitionsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => PoolDefinitionsQueryBuilder;
  find: () => Promise<PoolDefinitionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.QueryPoolDefinitions
 * @requiredField query
 */
export async function typedQueryPoolDefinitions(
  query: PoolDefinitionQuery,
  options?: QueryPoolDefinitionsOptions
): Promise<
  NonNullablePaths<
    QueryPoolDefinitionsResponse,
    | `poolDefinitions`
    | `poolDefinitions.${number}.displayName`
    | `poolDefinitions.${number}.details.creditConfiguration.amount`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.queryPoolDefinitions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PoolDefinitionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        'details.benefits.itemSetId',
        'details.benefits.providerAppId',
        'programDefinitionIds'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: ['_createdDate', '_id', 'namespace'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PoolDefinition,
  PoolDefinitionQuerySpec
>;
export type PoolDefinitionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.
  See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
  for more information.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  List of sort objects. 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order. Use `ASC` for ascending order or `DESC` for descending order.

  Default: `ASC`  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      PoolDefinition,
      PoolDefinitionQuerySpec,
      PoolDefinitionQuery
    >(),
  },
};

/**
 * Adds a pool definition to a program definition.
 *
 * Pool definitions can be contained by multiple program definitions, so this doesn't affect other program definitions that contain this pool definition.
 * @param poolDefinitionId - ID of the pool definition to add to the program definition.
 * @public
 * @requiredField options
 * @requiredField options.cascade
 * @requiredField options.programDefinitionId
 * @requiredField poolDefinitionId
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.AddPoolDefinitionToProgramDefinition
 */
export async function addPoolDefinitionToProgramDefinition(
  poolDefinitionId: string,
  options: NonNullablePaths<
    AddPoolDefinitionToProgramDefinitionOptions,
    `cascade` | `programDefinitionId`,
    2
  >
): Promise<
  NonNullablePaths<
    AddPoolDefinitionToProgramDefinitionResponse,
    | `poolDefinition.displayName`
    | `poolDefinition.programDefinitionIds`
    | `poolDefinition.details.benefits`
    | `poolDefinition.details.benefits.${number}.benefitKey`
    | `poolDefinition.details.creditConfiguration.amount`
    | `poolDefinition.programDefinitions`
    | `poolDefinition.programDefinitions.${number}._id`,
    6
  > & {
    __validationErrorsType?: AddPoolDefinitionToProgramDefinitionValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitionId: poolDefinitionId,
    programDefinitionId: options?.programDefinitionId,
    cascade: options?.cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.addPoolDefinitionToProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinitionId: '$[0]',
          programDefinitionId: '$[1].programDefinitionId',
          cascade: '$[1].cascade',
        },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddPoolDefinitionToProgramDefinitionOptions {
  /**
   * ID of the program definition to add the pool definition to.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition and pool definition will be applied to associated programs and pools. */
  cascade: CascadeWithLiterals;
}

/**
 * Removes a pool definition from a program definition.
 *
 * Pool definitions don't need to be contained by a program definition, so this doesn't affect the usability of this pool definition.
 * @param poolDefinitionId - ID of the pool definition to remove from the program definition.
 * @public
 * @requiredField options
 * @requiredField options.cascade
 * @requiredField options.programDefinitionId
 * @requiredField poolDefinitionId
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.RemovePoolDefinitionFromProgramDefinition
 */
export async function removePoolDefinitionFromProgramDefinition(
  poolDefinitionId: string,
  options: NonNullablePaths<
    RemovePoolDefinitionFromProgramDefinitionOptions,
    `cascade` | `programDefinitionId`,
    2
  >
): Promise<
  NonNullablePaths<
    RemovePoolDefinitionFromProgramDefinitionResponse,
    | `poolDefinition.displayName`
    | `poolDefinition.programDefinitionIds`
    | `poolDefinition.details.benefits`
    | `poolDefinition.details.benefits.${number}.benefitKey`
    | `poolDefinition.details.creditConfiguration.amount`
    | `poolDefinition.programDefinitions`
    | `poolDefinition.programDefinitions.${number}._id`,
    6
  > & {
    __validationErrorsType?: RemovePoolDefinitionFromProgramDefinitionValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolDefinitionId: poolDefinitionId,
    programDefinitionId: options?.programDefinitionId,
    cascade: options?.cascade,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.removePoolDefinitionFromProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolDefinitionId: '$[0]',
          programDefinitionId: '$[1].programDefinitionId',
          cascade: '$[1].cascade',
        },
        singleArgumentUnchanged: false,
      },
      ['poolDefinitionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemovePoolDefinitionFromProgramDefinitionOptions {
  /**
   * ID of the program definition to remove the pool definition from.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition and pool definition will be applied to associated programs and pools. */
  cascade: CascadeWithLiterals;
}

/**
 * Retrieve all a program definition's pool definitions.
 * @param programDefinitionId - ID of the program definition to retrieve its pool definitions.
 * @public
 * @requiredField options
 * @requiredField options.namespace
 * @requiredField programDefinitionId
 * @permissionId BENEFIT_PROGRAMS.POOL_DEFINITION_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.pool_definition.PoolDefinitionService.FindPoolDefinitionsByProgramDefinition
 */
export async function findPoolDefinitionsByProgramDefinition(
  programDefinitionId: string,
  options: NonNullablePaths<
    FindPoolDefinitionsByProgramDefinitionOptions,
    `namespace`,
    2
  >
): Promise<
  NonNullablePaths<
    FindPoolDefinitionsByProgramDefinitionResponse,
    | `poolDefinitions`
    | `poolDefinitions.${number}.displayName`
    | `poolDefinitions.${number}.details.creditConfiguration.amount`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programDefinitionId: programDefinitionId,
    namespace: options?.namespace,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1PoolDefinition.findPoolDefinitionsByProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          programDefinitionId: '$[0]',
          namespace: '$[1].namespace',
        },
        singleArgumentUnchanged: false,
      },
      ['programDefinitionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface FindPoolDefinitionsByProgramDefinitionOptions {
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   */
  namespace: string;
}
